import { motion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";
import { stats } from "@/data/site";
import { IDS } from "@/constants/testIds";
import { useQuote } from "@/components/site/QuoteDialog";
import Counter from "@/components/site/Counter";
import { EASE } from "@/components/site/Reveal";

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { open: openQuote } = useQuote();

  return (
    <section
      id="home"
      data-testid={IDS.heroSection}
      className="relative min-h-screen flex items-center overflow-hidden bg-cream grain"
    >
      {/* Faint editorial grid + soft sage aura */}
      <div className="absolute inset-0 bg-grid-faint" />
      <div className="absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-sage/70 blur-3xl" />
      <div className="absolute bottom-0 -left-48 h-[26rem] w-[26rem] rounded-full bg-sage/50 blur-3xl" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 lg:pt-36">
        <div className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full bg-sage px-4 py-2 text-[11px] uppercase tracking-[0.24em] font-semibold text-forest"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
            </span>
            Merchant Exporter · India
          </motion.span>

          <h1 className="mt-7 font-heading font-bold text-ink text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-7xl tracking-tight">
            <MaskedLine delay={0.15}>Sourcing Sustainable</MaskedLine>
            <MaskedLine delay={0.27}>Packaging Solutions</MaskedLine>
            <MaskedLine delay={0.39}>
              <span className="font-serif-accent italic font-medium text-brand">
                from India.
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
            className="mt-7 text-base sm:text-lg text-mist max-w-xl leading-relaxed"
          >
            We help importers, distributors, wholesalers, retailers, and global
            brands source premium eco-friendly paper packaging and compostable
            products from trusted Indian manufacturing partners.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.68 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={openQuote}
              data-testid={IDS.heroPrimaryCta}
              className="group inline-flex items-center gap-2 rounded-full bg-brand text-white hover:bg-forest px-7 py-4 text-sm font-semibold shadow-lg shadow-brand/25 transition-all duration-300 hover:-translate-y-0.5"
            >
              Request Export Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              onClick={() =>
                import("sonner").then(({ toast }) =>
                  toast.info(
                    "Catalogue will be available shortly — please request via WhatsApp or the enquiry form."
                  )
                )
              }
              data-testid={IDS.heroSecondaryCta}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 hover:border-brand hover:text-brand bg-white/60 backdrop-blur px-7 py-4 text-sm font-semibold text-ink transition-all duration-300"
            >
              <Download className="h-4 w-4" /> Download Catalogue
            </button>
          </motion.div>
        </div>

        {/* Stats strip */}
        <motion.dl
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-line pt-8"
        >
          {stats.map((s) => (
            <div key={s.label}>
              <dt className="sr-only">{s.label}</dt>
              <dd className="font-heading text-2xl sm:text-3xl font-bold text-forest">
                <Counter to={s.value} />
                <span className="text-brand">{s.suffix}</span>
              </dd>
              <dd className="mt-1 text-[11px] uppercase tracking-[0.14em] text-mist">
                {s.label}
              </dd>
            </div>
          ))}
        </motion.dl>
      </div>
    </section>
  );
}
